<script lang="ts">
import type {
	ButtonSlots,
	ButtonProps as UButtonProps,
} from '#ui/types';
import { tv } from 'tailwind-variants';
import theme from '#build/ui/button';
import { pick } from '#layers/base/public';

export interface ButtonProps extends UButtonProps {
	active?: boolean;
	activeClass?: string;
	pressed?: boolean;
	rounded?: boolean;
	form?: string;
	readonly?: boolean;
	indicator?: boolean;
}

const button = tv({
	variants: {
		...pick(theme.variants, ['color', 'variant']),
		pressed: {
			true: '',
		},
		rounded: {
			true: 'rounded-full',
		},
	},
	compoundVariants: [
		{
			pressed: true,
			color: 'neutral',
			variant: 'outline',
			class: 'bg-elevated text-highlighted',
		},
		{
			pressed: true,
			color: 'neutral',
			variant: 'ghost',
			class: 'bg-elevated text-highlighted!',
		},
		{
			pressed: true,
			color: 'neutral',
			variant: 'link',
			class: 'text-highlighted!',
		},
	],
});
</script>

<script setup lang="ts">
import { useForwardProps } from 'reka-ui';
import { computed } from 'vue';

import { useReadonly } from '#layers/ui/composables/use-readonly';
import Button from '#ui/components/Button.vue';

const {
	active,
	activeClass,
	pressed,
	rounded,
	readonly,
	indicator,
	variant,
	color,
	...props
} = defineProps<ButtonProps>();
const slots = defineSlots<ButtonSlots>();

const forwarded = useForwardProps(computed(() => ({
	...props,
	activeClass,
	variant,
	color,
})));

const { preventKeyboardNavigation, preventPointer } = useReadonly(() => readonly);

function preventReadonlyClick(event: MouseEvent) {
	preventPointer(event);
}

const classes = computed(() => [
	button({
		pressed,
		rounded,
		variant,
		color,
	}),
	active ? activeClass : undefined,
]);
</script>

<template>
	<Button
		v-bind="forwarded"
		:class="classes"
		:readonly
		:data-readonly="readonly || undefined"
		:data-indicator="indicator || undefined"
		@pointerdown.capture="preventPointer"
		@click.capture="preventReadonlyClick"
		@keydown.capture="preventKeyboardNavigation"
	>
		<template v-for="(_, name) in slots" #[name]="slotData: any">
			<slot :name v-bind="slotData" />
		</template>
	</Button>
</template>
